import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Typography, Box, Alert,
  Tabs, Tab, Paper, IconButton, Tooltip, Switch,
  Autocomplete, Divider, Stack, Collapse, FormControlLabel, Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Leaderboard as LeaderboardIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';
import { ComplianceTag } from '../types';
import { format } from 'date-fns';

const CATEGORY_ORDER: Record<string, number> = { diet: 0, allergen: 1, ailment: 2, custom: 3 };
const CATEGORY_LABELS: Record<string, string> = { diet: 'Diet', allergen: 'Allergen', ailment: 'Condition', custom: 'Custom' };

const STATUS_OPTIONS = ['upcoming', 'active', 'completed'];
const PRIZE_DISTRIBUTION_OPTIONS = [
  { value: 'top_winners', label: 'Top Winners (equal prize each)' },
  { value: 'equal_split', label: 'Equal Split (prize pool divided)' },
  { value: 'random_draw', label: 'Random Draw (lottery)' },
];

// ─── Prize distribution helpers ─────────────────────────────────────────────

/** Convert admin UI enum → JSON payout map stored in DB */
function buildPrizeDistributionJson(
  mode: string,
  prizePerWinner: number,
  totalWinners: number,
): string {
  if (mode === 'top_winners') {
    const map: Record<string, number> = {};
    for (let i = 1; i <= Math.max(1, totalWinners); i++) {
      map[String(i)] = prizePerWinner;
    }
    return JSON.stringify(map);
  }
  if (mode === 'equal_split') {
    const total = prizePerWinner * totalWinners;
    const map: Record<string, number> = {};
    for (let i = 1; i <= Math.max(1, totalWinners); i++) {
      map[String(i)] = parseFloat((total / totalWinners).toFixed(2));
    }
    return JSON.stringify(map);
  }
  if (mode === 'random_draw') {
    return JSON.stringify({ draw: prizePerWinner * totalWinners });
  }
  return mode; // fallback: store as-is
}

/** Detect enum value from stored DB value.
 *  Handles: parsed JSONB object, JSON string, or legacy plain-text enum. */
function parsePrizeDistributionMode(stored: unknown): string {
  if (!stored) return 'top_winners';

  // Already a parsed object (JSONB column returns JS objects via node-postgres)
  if (typeof stored === 'object') {
    const obj = stored as Record<string, unknown>;
    if ('draw' in obj) return 'random_draw';
    if ('split' in obj) return 'equal_split';
    // numeric keys → per-place top_winners map
    return 'top_winners';
  }

  // String path
  const trimmed = String(stored).trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if ('draw' in parsed) return 'random_draw';
      if ('split' in parsed) return 'equal_split';
      return 'top_winners';
    } catch {
      return 'top_winners';
    }
  }
  if (['top_winners', 'equal_split', 'random_draw'].includes(trimmed)) return trimmed;
  return 'top_winners';
}

// ─── Form state ──────────────────────────────────────────────────────────────

interface ChallengeRules {
  min_meals_required: number;
  min_recipes_submitted: number;
  require_recipe_submission: boolean;
  bonus_for_family_logging: boolean;
}

interface ChallengeFormState {
  challenge_name: string;
  description: string;
  status: string;
  start_date: string;
  end_date: string;
  prize_amount: number;
  prize_distribution: string; // UI enum: top_winners | equal_split | random_draw
  total_winners: number;
  required_tags: string[];
  tag_match_mode: string;
  image_url: string;
  rules: ChallengeRules;
}

const emptyRules: ChallengeRules = {
  min_meals_required: 0,
  min_recipes_submitted: 0,
  require_recipe_submission: false,
  bonus_for_family_logging: false,
};

const emptyForm: ChallengeFormState = {
  challenge_name: '',
  description: '',
  status: 'upcoming',
  start_date: '',
  end_date: '',
  prize_amount: 0,
  prize_distribution: 'top_winners',
  total_winners: 5,
  required_tags: [],
  tag_match_mode: 'any',
  image_url: '',
  rules: emptyRules,
};

interface PersonalChallengeFormState {
  patient_id: string;
  dietitian_id: string;
  challenge_name: string;
  description: string;
  personal_goal: string;
  duration_days: number;
  bonus_points: number;
  required_tags: string[];
  tag_match_mode: string;
}

const emptyPersonalForm: PersonalChallengeFormState = {
  patient_id: '',
  dietitian_id: '',
  challenge_name: '',
  description: '',
  personal_goal: '',
  duration_days: 7,
  bonus_points: 0,
  required_tags: [],
  tag_match_mode: 'any',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatEvalRule(tag: ComplianceTag): string | null {
  const parts: string[] = [];
  if (tag.evaluation_type === 'nutrition' || tag.evaluation_type === 'both') {
    if (tag.nutrition_nutrient && tag.nutrition_threshold != null) {
      const cmp = tag.nutrition_comparison === 'less_than' ? '<' : tag.nutrition_comparison === 'greater_than' ? '>' : '~';
      parts.push(`${tag.nutrition_nutrient} ${cmp} ${tag.nutrition_threshold}${tag.nutrition_unit || ''}/serving`);
    }
  }
  if (tag.evaluation_type === 'ingredient' || tag.evaluation_type === 'both') {
    const kw = tag.ingredient_keywords;
    if (kw) {
      const count = kw.split(',').length;
      parts.push(`excludes ${count} ingredient keyword${count > 1 ? 's' : ''}`);
    }
  }
  if (tag.evaluation_type === 'ai_only') parts.push('AI-evaluated');
  return parts.length > 0 ? parts.join(' + ') : null;
}

function statusColor(s: string): 'success' | 'warning' | 'default' | 'error' {
  if (s === 'active') return 'success';
  if (s === 'upcoming') return 'warning';
  if (s === 'completed') return 'default';
  return 'error';
}

function formatPrizePreview(mode: string, prizePerWinner: number, totalWinners: number): string {
  if (mode === 'random_draw') return `$${(prizePerWinner * totalWinners).toFixed(2)} pool → 1 random winner`;
  if (mode === 'equal_split') {
    const each = (prizePerWinner * totalWinners / Math.max(1, totalWinners)).toFixed(2);
    return `$${each} each × ${totalWinners} winners (split)`;
  }
  return `$${prizePerWinner} each × ${totalWinners} winners`;
}

// ─── Main component ──────────────────────────────────────────────────────────

export function ChallengesTab() {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ChallengeFormState>(emptyForm);
  const [leaderboardId, setLeaderboardId] = useState<string | null>(null);
  const [leaderboardName, setLeaderboardName] = useState('');
  const [showAliases, setShowAliases] = useState(false);
  const [showRules, setShowRules] = useState(false);

  // Personal challenge state
  const [personalFormOpen, setPersonalFormOpen] = useState(false);
  const [personalForm, setPersonalForm] = useState<PersonalChallengeFormState>(emptyPersonalForm);

  // ─── Queries ──────────────────────────────────────────────────────────────

  const { data: challenges = [], isLoading: loadingChallenges, error: challengeError } = useQuery({
    queryKey: ['admin-challenges'],
    queryFn: () => adminApi.getChallenges(),
    refetchInterval: 30000,
  });

  const { data: personalChallenges = [], isLoading: loadingPersonal } = useQuery({
    queryKey: ['admin-personal-challenges'],
    queryFn: () => adminApi.getPersonalChallenges(),
    enabled: subTab === 1,
  });

  const { data: complianceData } = useQuery({
    queryKey: ['compliance-tags-for-challenges'],
    queryFn: () => adminApi.getComplianceTags({ status: 'reviewed' }),
    staleTime: 60000,
  });

  const { data: inquiries = [] } = useQuery({
    queryKey: ['patient-inquiries-for-challenges'],
    queryFn: () => adminApi.getPatientInquiries(),
    enabled: subTab === 1,
    staleTime: 60000,
  });

  // Active patients (have a Healthie account)
  const activePatients = useMemo(() =>
    inquiries.filter((inq: any) => inq.healthie_patient_id),
    [inquiries]
  );

  const tagOptions = useMemo(() => {
    if (!complianceData?.tags) return [];
    return [...complianceData.tags]
      .sort((a, b) => (CATEGORY_ORDER[a.tag_category] ?? 9) - (CATEGORY_ORDER[b.tag_category] ?? 9) || a.display_name.localeCompare(b.display_name))
      .map(t => t.ncm_tag_id);
  }, [complianceData]);

  const tagLookup = useMemo(() => {
    const map = new Map<string, ComplianceTag>();
    complianceData?.tags?.forEach(t => map.set(t.ncm_tag_id, t));
    return map;
  }, [complianceData]);

  const { data: pointRules = [], isLoading: loadingRules } = useQuery({
    queryKey: ['admin-point-rules'],
    queryFn: () => adminApi.getPointRules(),
  });

  const { data: leaderboard = [], isLoading: loadingLeaderboard } = useQuery({
    queryKey: ['admin-challenge-leaderboard', leaderboardId],
    queryFn: () => (leaderboardId ? adminApi.getChallengeLeaderboard(leaderboardId) : Promise.resolve([])),
    enabled: !!leaderboardId,
  });

  // ─── Mutations ────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (data: any) => adminApi.createChallenge(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-challenges'] }); closeForm(); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateChallenge(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-challenges'] }); closeForm(); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteChallenge(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-challenges'] }),
  });

  const updateRuleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updatePointRule(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-point-rules'] }),
  });

  const createPersonalMutation = useMutation({
    mutationFn: (data: any) => adminApi.createPersonalChallenge(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-personal-challenges'] });
      setPersonalFormOpen(false);
      setPersonalForm(emptyPersonalForm);
    },
  });

  // ─── Form helpers ─────────────────────────────────────────────────────────

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setShowRules(false);
    setFormOpen(true);
  }

  function openEdit(challenge: any) {
    // eligibility_criteria may be a JSONB object or a JSON string
    const criteriaRaw = challenge.eligibility_criteria;
    const criteria: any = !criteriaRaw
      ? {}
      : typeof criteriaRaw === 'object'
        ? criteriaRaw
        : (() => { try { return JSON.parse(criteriaRaw); } catch { return {}; } })();

    // rules may be a JSONB object or a JSON string
    const rulesRaw = challenge.rules;
    const rulesObj: any = !rulesRaw
      ? {}
      : typeof rulesRaw === 'object'
        ? rulesRaw
        : (() => { try { return JSON.parse(rulesRaw); } catch { return {}; } })();

    const parsedRules: ChallengeRules = {
      min_meals_required: rulesObj.min_meals_required || 0,
      min_recipes_submitted: rulesObj.min_recipes_submitted || 0,
      require_recipe_submission: !!rulesObj.require_recipe_submission,
      bonus_for_family_logging: !!rulesObj.bonus_for_family_logging,
    };

    // start_date / end_date may be ISO strings or Date objects
    const toDateStr = (v: any) => {
      if (!v) return '';
      if (typeof v === 'string') return v.split('T')[0];
      if (v instanceof Date) return v.toISOString().split('T')[0];
      return String(v).split('T')[0];
    };

    setEditingId(challenge.id);
    setForm({
      challenge_name: challenge.challenge_name || '',
      description: challenge.description || '',
      status: challenge.status || 'upcoming',
      start_date: toDateStr(challenge.start_date),
      end_date: toDateStr(challenge.end_date),
      prize_amount: challenge.prize_amount || 0,
      prize_distribution: parsePrizeDistributionMode(challenge.prize_distribution),
      total_winners: challenge.total_winners || 5,
      required_tags: criteria.required_tags || [],
      tag_match_mode: criteria.tag_match_mode || 'any',
      image_url: challenge.image_url || '',
      rules: parsedRules,
    });
    const hasRules = parsedRules.min_meals_required > 0 || parsedRules.min_recipes_submitted > 0
      || parsedRules.require_recipe_submission || parsedRules.bonus_for_family_logging;
    setShowRules(hasRules);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setShowRules(false);
  }

  function handleSave() {
    // Build rules object — omit empty/false values to keep DB clean
    const rulesObj: Record<string, any> = {};
    if (form.rules.min_meals_required > 0) rulesObj.min_meals_required = form.rules.min_meals_required;
    if (form.rules.min_recipes_submitted > 0) rulesObj.min_recipes_submitted = form.rules.min_recipes_submitted;
    if (form.rules.require_recipe_submission) rulesObj.require_recipe_submission = true;
    if (form.rules.bonus_for_family_logging) rulesObj.bonus_for_family_logging = true;

    const payload: any = {
      challenge_name: form.challenge_name,
      description: form.description,
      status: form.status,
      start_date: form.start_date,
      end_date: form.end_date,
      prize_amount: form.prize_amount,
      // Store the proper per-place JSON map instead of a bare string enum
      prize_distribution: buildPrizeDistributionJson(form.prize_distribution, form.prize_amount, form.total_winners),
      total_winners: form.total_winners,
      image_url: form.image_url || null,
      rules: rulesObj,
      eligibility_criteria: form.required_tags.length > 0
        ? { required_tags: form.required_tags, tag_match_mode: form.tag_match_mode }
        : {},
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleDelete(id: string, name: string, participantCount: number) {
    if (participantCount > 0) {
      alert(`Cannot delete "${name}" — it has ${participantCount} participant(s). Set status to "completed" instead.`);
      return;
    }
    if (window.confirm(`Delete challenge "${name}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  }

  function handleSavePersonal() {
    if (!personalForm.patient_id || !personalForm.challenge_name) return;
    createPersonalMutation.mutate({
      challenge_name: personalForm.challenge_name,
      description: personalForm.description || null,
      patient_id: personalForm.patient_id,
      dietitian_id: personalForm.dietitian_id || 'admin',
      personal_goal: personalForm.personal_goal || null,
      duration_days: personalForm.duration_days,
      bonus_points: personalForm.bonus_points,
      required_tags: personalForm.required_tags.length > 0 ? personalForm.required_tags : undefined,
      tag_match_mode: personalForm.tag_match_mode,
    });
  }

  // ─── Shared tag autocomplete ───────────────────────────────────────────────

  function renderTagAutocomplete(
    value: string[],
    onChange: (v: string[]) => void,
    label = 'Required Diet Tags',
  ) {
    return (
      <Autocomplete
        multiple
        options={tagOptions}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        groupBy={(option) => {
          const tag = tagLookup.get(option);
          return tag ? (CATEGORY_LABELS[tag.tag_category] || 'Other') : 'Other';
        }}
        getOptionLabel={(option) => {
          const tag = tagLookup.get(option);
          return tag ? `${tag.display_name} (${option})` : option;
        }}
        renderInput={(params) => <TextField {...params} label={label} placeholder="Select tags…" />}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const tag = tagLookup.get(option);
            return (
              <Chip
                variant="outlined" size="small"
                label={tag?.display_name || option}
                {...getTagProps({ index })}
                key={option}
              />
            );
          })
        }
        freeSolo
        filterSelectedOptions
      />
    );
  }

  // ─── Render: Challenges List ───────────────────────────────────────────────

  function renderChallengesList() {
    if (loadingChallenges) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    if (challengeError) {
      const msg = (challengeError as any)?.message ?? String(challengeError);
      return <Alert severity="error">Failed to load challenges: {msg}</Alert>;
    }
    // Safety: log each challenge row shape on first load so we can diagnose field mismatches
    if (challenges.length > 0) {
      console.debug('[ChallengesTab] First challenge sample:', JSON.stringify(challenges[0]).substring(0, 400));
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
          <Typography variant="h6">
            <TrophyIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#FF8C00' }} />
            Cook-Off Challenges ({challenges.length})
          </Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-challenges'] })} sx={{ mr: 1 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New Challenge
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Prize Payout</TableCell>
                <TableCell>Diet Tags</TableCell>
                <TableCell align="right">Participants</TableCell>
                <TableCell align="right">Total Points</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {challenges.map((c: any) => {
                // eligibility_criteria can be a parsed JSONB object or a JSON string
                const criteriaObj: any = !c.eligibility_criteria
                  ? {}
                  : typeof c.eligibility_criteria === 'object'
                    ? c.eligibility_criteria
                    : (() => { try { return JSON.parse(c.eligibility_criteria); } catch { return {}; } })();
                const tags: string[] = criteriaObj?.required_tags || [];
                const distMode = parsePrizeDistributionMode(c.prize_distribution);
                return (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{c.challenge_name}</Typography>
                      {c.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip label={c.status} size="small" color={statusColor(c.status)} /></TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {c.start_date ? format(new Date(c.start_date), 'MMM d') : '?'} – {c.end_date ? format(new Date(c.end_date), 'MMM d, yyyy') : '?'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatPrizePreview(distMode, c.prize_amount, c.total_winners)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {tags.length > 0
                        ? tags.map((t: string) => <Chip key={t} label={t} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />)
                        : <Chip label="Open (all)" size="small" color="info" variant="outlined" />}
                    </TableCell>
                    <TableCell align="right">{c.participant_count || 0}</TableCell>
                    <TableCell align="right">{c.total_points_awarded || 0}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Leaderboard">
                        <IconButton size="small" onClick={() => { setLeaderboardId(c.id); setLeaderboardName(c.challenge_name); }}>
                          <LeaderboardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(c)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(c.id, c.challenge_name, c.participant_count)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {challenges.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" py={3}>No challenges found</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  // ─── Render: Personal Challenges ──────────────────────────────────────────

  function renderPersonalChallenges() {
    if (loadingPersonal) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
          <Typography variant="h6">
            <PersonIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#4CAF50' }} />
            Personal Challenges ({personalChallenges.length})
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setPersonalFormOpen(true)} color="success">
            Assign to Patient
          </Button>
        </Box>

        {activePatients.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No activated patients yet. Activate a patient via the Patient Inquiries tab first.
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Challenge</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Goal</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Bonus Points</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Points Earned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personalChallenges.map((c: any) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{c.challenge_name}</Typography>
                    {c.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{c.patient_id?.substring(0, 12)}…</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{c.personal_goal || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {c.start_date ? format(new Date(c.start_date), 'MMM d') : '?'} – {c.end_date ? format(new Date(c.end_date), 'MMM d') : '?'}
                    </Typography>
                  </TableCell>
                  <TableCell>{c.prize_amount || 0} pts</TableCell>
                  <TableCell><Chip label={c.status} size="small" color={statusColor(c.status)} /></TableCell>
                  <TableCell align="right">{c.patient_points || 0}</TableCell>
                </TableRow>
              ))}
              {personalChallenges.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>No personal challenges assigned yet</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  // ─── Render: Point Rules ──────────────────────────────────────────────────

  function renderPointRules() {
    if (loadingRules) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
          <Typography variant="h6">SOL Point Rules</Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-point-rules'] })}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Activity Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Base Points</TableCell>
                <TableCell align="right">Max/Day</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pointRules.map((rule: any) => (
                <TableRow key={rule.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{rule.activity_type}</Typography>
                  </TableCell>
                  <TableCell>{rule.description || '—'}</TableCell>
                  <TableCell align="right">{rule.base_points}</TableCell>
                  <TableCell align="right">{rule.max_per_day ?? '∞'}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={rule.is_active}
                      size="small"
                      onChange={() =>
                        updateRuleMutation.mutate({ id: rule.id, data: { is_active: !rule.is_active } })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <PointRuleEditButton rule={rule} onSave={(id, data) => updateRuleMutation.mutate({ id, data })} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Challenges" />
        <Tab label="Personal" />
        <Tab label="Point Rules" />
      </Tabs>

      {subTab === 0 && renderChallengesList()}
      {subTab === 1 && renderPersonalChallenges()}
      {subTab === 2 && renderPointRules()}

      {/* ─── Create / Edit Challenge Dialog ─── */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Challenge' : 'Create Challenge'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Challenge Name" fullWidth required
              value={form.challenge_name}
              onChange={(e) => setForm({ ...form, challenge_name: e.target.value })}
            />
            <TextField
              label="Description" fullWidth multiline rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Status" select fullWidth
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField
                label="Start Date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
              <TextField
                label="End Date" type="date" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </Box>

            <Divider />
            <Typography variant="subtitle2">Prize Configuration</Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Prize per Winner ($)" type="number" fullWidth
                value={form.prize_amount}
                onChange={(e) => setForm({ ...form, prize_amount: Number(e.target.value) })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
              />
              <TextField
                label="Total Winners" type="number" fullWidth
                value={form.total_winners}
                onChange={(e) => setForm({ ...form, total_winners: Number(e.target.value) })}
              />
              <TextField
                label="Distribution Mode" select fullWidth
                value={form.prize_distribution}
                onChange={(e) => setForm({ ...form, prize_distribution: e.target.value })}
              >
                {PRIZE_DISTRIBUTION_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Box>

            {/* Prize preview */}
            <Alert severity="info" sx={{ py: 0.5 }}>
              <strong>Payout preview:</strong> {formatPrizePreview(form.prize_distribution, form.prize_amount, form.total_winners)}
              {' '}— total pool: <strong>${(form.prize_amount * form.total_winners).toFixed(2)}</strong>
            </Alert>

            <Divider />

            {/* Diet Tag Requirements */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Diet Tag Requirements</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Only recipes/meals matching these tags count toward challenge points. Leave empty for open challenges.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 2 }}>
                {renderTagAutocomplete(
                  form.required_tags,
                  (v) => { setForm({ ...form, required_tags: v }); setShowAliases(v.length > 0); }
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Tag Match Mode" select fullWidth
                  value={form.tag_match_mode}
                  onChange={(e) => setForm({ ...form, tag_match_mode: e.target.value })}
                  helperText={form.tag_match_mode === 'any' ? 'Match if ANY tag overlaps' : 'ALL tags must be present'}
                >
                  <MenuItem value="any">Any (OR)</MenuItem>
                  <MenuItem value="all">All (AND)</MenuItem>
                </TextField>
              </Box>
            </Box>

            {form.required_tags.length > 0 && (
              <Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 0.5 }}
                  onClick={() => setShowAliases(!showAliases)}
                >
                  {showAliases ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    {showAliases ? 'Hide' : 'Show'} tag details & aliases ({form.required_tags.length} tag{form.required_tags.length > 1 ? 's' : ''})
                  </Typography>
                </Box>
                <Collapse in={showAliases}>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                    {form.required_tags.map(tagId => {
                      const tag = tagLookup.get(tagId);
                      const aliases = tag?.alternate_names?.split(',').map(a => a.trim()).filter(Boolean) || [];
                      const evalDesc = tag ? formatEvalRule(tag) : null;
                      return (
                        <Box key={tagId} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">{tag?.display_name || tagId}</Typography>
                            <Chip label={tag?.tag_category || '?'} size="small" variant="outlined" />
                            <Chip label={tag?.evaluation_type || '?'} size="small" variant="outlined" color="info" />
                          </Box>
                          {evalDesc && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Rule: {evalDesc}
                            </Typography>
                          )}
                          {aliases.length > 0 && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Also matches: </Typography>
                              {aliases.map(a => <Chip key={a} label={a} size="small" sx={{ mr: 0.5, mb: 0.25, height: 20, fontSize: 11 }} />)}
                            </Box>
                          )}
                          {aliases.length === 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                              No aliases configured
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Manage aliases and evaluation rules in the <strong>Compliance Review</strong> tab.
                    </Typography>
                  </Paper>
                </Collapse>
              </Box>
            )}

            <Divider />

            {/* Participation rules */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setShowRules(!showRules)}
            >
              {showRules ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ ml: 0.5 }}>
                Participation Requirements {showRules ? '' : '(optional)'}
              </Typography>
            </Box>
            <Collapse in={showRules}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="caption" color="text.secondary">
                    These rules determine eligibility for the prize. Users who don't meet minimums are shown as ineligible on the leaderboard.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Min meals logged to qualify" type="number" fullWidth
                      value={form.rules.min_meals_required}
                      onChange={(e) => setForm({ ...form, rules: { ...form.rules, min_meals_required: Number(e.target.value) } })}
                      helperText="0 = no minimum"
                    />
                    <TextField
                      label="Min recipes submitted" type="number" fullWidth
                      value={form.rules.min_recipes_submitted}
                      onChange={(e) => setForm({ ...form, rules: { ...form.rules, min_recipes_submitted: Number(e.target.value) } })}
                      helperText="0 = no minimum"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.rules.require_recipe_submission}
                          onChange={(e) => setForm({ ...form, rules: { ...form.rules, require_recipe_submission: e.target.checked } })}
                        />
                      }
                      label="Must submit at least 1 recipe to qualify"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.rules.bonus_for_family_logging}
                          onChange={(e) => setForm({ ...form, rules: { ...form.rules, bonus_for_family_logging: e.target.checked } })}
                        />
                      }
                      label="Bonus points when whole family logs a meal"
                    />
                  </Box>
                </Stack>
              </Paper>
            </Collapse>

            <TextField
              label="Image URL (optional)" fullWidth
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.challenge_name || !form.start_date || !form.end_date || createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? <CircularProgress size={20} /> : editingId ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Create Personal Challenge Dialog ─── */}
      <Dialog open={personalFormOpen} onClose={() => setPersonalFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <PersonIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Assign Personal Challenge
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Select Patient" select fullWidth required
              value={personalForm.patient_id}
              onChange={(e) => setPersonalForm({ ...personalForm, patient_id: e.target.value })}
              helperText={activePatients.length === 0 ? 'No activated patients — activate from Patient Inquiries first' : ''}
            >
              {activePatients.map((p: any) => (
                <MenuItem key={p.id} value={p.famelia_user_id || String(p.id)}>
                  {p.full_name} — {p.email}
                </MenuItem>
              ))}
              {activePatients.length === 0 && (
                <MenuItem disabled value="">No activated patients yet</MenuItem>
              )}
            </TextField>

            <TextField
              label="Dietitian ID (your Healthie ID or 'admin')" fullWidth
              value={personalForm.dietitian_id}
              onChange={(e) => setPersonalForm({ ...personalForm, dietitian_id: e.target.value })}
              helperText="Leave blank to default to 'admin'"
            />

            <TextField
              label="Challenge Name" fullWidth required
              value={personalForm.challenge_name}
              onChange={(e) => setPersonalForm({ ...personalForm, challenge_name: e.target.value })}
              placeholder="e.g. 'Log 5 gluten-free meals this week'"
            />

            <TextField
              label="Description" fullWidth multiline rows={2}
              value={personalForm.description}
              onChange={(e) => setPersonalForm({ ...personalForm, description: e.target.value })}
            />

            <TextField
              label="Personal Goal" fullWidth
              value={personalForm.personal_goal}
              onChange={(e) => setPersonalForm({ ...personalForm, personal_goal: e.target.value })}
              placeholder="e.g. 'Reduce sodium intake to under 2000mg/day'"
              helperText="This goal description is shown to the patient in the app"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Duration (days)" type="number" fullWidth
                value={personalForm.duration_days}
                onChange={(e) => setPersonalForm({ ...personalForm, duration_days: Number(e.target.value) })}
                helperText="Challenge starts immediately"
              />
              <TextField
                label="Bonus SOL Points" type="number" fullWidth
                value={personalForm.bonus_points}
                onChange={(e) => setPersonalForm({ ...personalForm, bonus_points: Number(e.target.value) })}
                helperText="Awarded on completion"
              />
            </Box>

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Diet Tags (optional)</Typography>
            {renderTagAutocomplete(
              personalForm.required_tags,
              (v) => setPersonalForm({ ...personalForm, required_tags: v }),
              'Diet Tag Restrictions'
            )}
            {personalForm.required_tags.length > 0 && (
              <TextField
                label="Tag Match Mode" select fullWidth
                value={personalForm.tag_match_mode}
                onChange={(e) => setPersonalForm({ ...personalForm, tag_match_mode: e.target.value })}
              >
                <MenuItem value="any">Any (OR)</MenuItem>
                <MenuItem value="all">All (AND)</MenuItem>
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonalFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSavePersonal}
            disabled={!personalForm.patient_id || !personalForm.challenge_name || createPersonalMutation.isPending}
          >
            {createPersonalMutation.isPending ? <CircularProgress size={20} /> : 'Assign Challenge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Leaderboard Dialog ─── */}
      <Dialog open={!!leaderboardId} onClose={() => setLeaderboardId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <LeaderboardIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Leaderboard: {leaderboardName}
        </DialogTitle>
        <DialogContent>
          {loadingLeaderboard ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
          ) : leaderboard.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>No participants yet</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell align="right">Points</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="center">Eligible</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboard.map((entry: any, i: number) => (
                  <TableRow key={entry.user_id}>
                    <TableCell>{entry.ranking || i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                        {entry.user_id?.substring(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{entry.total_points}</TableCell>
                    <TableCell>{entry.joined_at ? format(new Date(entry.joined_at), 'MMM d') : '—'}</TableCell>
                    <TableCell align="center">{entry.is_eligible ? '✓' : '✗'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaderboardId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Inline Point Rule Edit Button ───────────────────────────────────────────

function PointRuleEditButton({ rule, onSave }: { rule: any; onSave: (id: string, data: any) => void }) {
  const [open, setOpen] = useState(false);
  const [basePoints, setBasePoints] = useState(rule.base_points);
  const [maxPerDay, setMaxPerDay] = useState(rule.max_per_day ?? '');

  return (
    <>
      <Tooltip title="Edit Rule">
        <IconButton size="small" onClick={() => { setBasePoints(rule.base_points); setMaxPerDay(rule.max_per_day ?? ''); setOpen(true); }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Point Rule: {rule.activity_type}</DialogTitle>
        <DialogContent>
          <TextField
            label="Base Points" type="number" fullWidth sx={{ mt: 1, mb: 2 }}
            value={basePoints} onChange={(e) => setBasePoints(Number(e.target.value))}
          />
          <TextField
            label="Max Per Day (blank = unlimited)" type="number" fullWidth
            value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            onSave(rule.id, { base_points: basePoints, max_per_day: maxPerDay === '' ? null : maxPerDay });
            setOpen(false);
          }}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
